import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  TableContainer,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line
} from 'recharts';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import { useContext } from 'react';
import HeaderMenus from './HeaderMenus';
import DataAgentChat from './DataAgent/DataAgentChat';

const ZinniaxReconciliationDashboard = () => {
  const [data, setData] = useState(null);
  const [jobQueue, setJobQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);
  const [error, setError] = useState(null);
  const [daysFilter, setDaysFilter] = useState(30);
  const { accessToken } = useContext(AppContext);
  
  // Sorting state for Job Queue
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedRawData, setSelectedRawData] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);

  const handleViewRawData = async (zinniaxCaseId) => {
    onOpen();
    setRawLoading(true);
    setSelectedRawData(null);
    try {
      const response = await callApi(`/api/internal/zinniax/case/${zinniaxCaseId}/raw`, accessToken, { method: 'GET' });
      if (response && response.ok) {
        const jsonData = await response.json();
        setSelectedRawData(jsonData);
      } else {
        setSelectedRawData({ error: 'Failed to load raw data' });
      }
    } catch (err) {
      console.error('Error fetching raw data:', err);
      setSelectedRawData({ error: 'An error occurred while fetching data' });
    } finally {
      setRawLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedJobQueue = () => {
    if (!jobQueue || jobQueue.length === 0) return [];
    
    const sortedJobs = [...jobQueue].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'case_no':
          aValue = a.payload?.case_no || '';
          bValue = b.payload?.case_no || '';
          break;
        case 'zinniax_case_id':
          aValue = a.payload?.zinniax_case_id || '';
          bValue = b.payload?.zinniax_case_id || '';
          break;
        case 'date_of_surgery':
          aValue = a.enriched_data?.date_of_surgery ? new Date(a.enriched_data.date_of_surgery).getTime() : 0;
          bValue = b.enriched_data?.date_of_surgery ? new Date(b.enriched_data.date_of_surgery).getTime() : 0;
          break;
        case 'payer_type':
          aValue = a.enriched_data?.payer_type || '';
          bValue = b.enriched_data?.payer_type || '';
          break;
        case 'carrier_name':
          aValue = a.enriched_data?.carrier_name || '';
          bValue = b.enriched_data?.carrier_name || '';
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sortedJobs;
  };

  const fetchJobQueue = async () => {
    try {
      setQueueLoading(true);
      const response = await callApi('/api/internal/zinniax/jobs/enriched?limit=0', accessToken, { method: 'GET' });
      if (response && response.ok) {
        const jsonData = await response.json();
        setJobQueue(jsonData.jobs || []);
      }
    } catch (err) {
      console.error('Error fetching job queue:', err);
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!accessToken) return;
        
        const response = await callApi(`/api/internal/zinniax/reconciliation-dashboard?days=${daysFilter}`, accessToken, { method: 'GET' });
        
        if (response && response.ok) {
          const jsonData = await response.json();
          setData(jsonData);
        } else {
          throw new Error('Failed to fetch data');
        }
      } catch (err) {
        console.error('Error fetching reconciliation data:', err);
        setError('Failed to load reconciliation data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken, daysFilter]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data || !data.variance || !data.time_series || !data.payer_leakage || !data.detailed_leakage) return null;

  const { variance, time_series, payer_leakage, detailed_leakage, no_claims_list, no_claims_insurance } = data;

  const escapeCSV = (str) => {
    if (str === null || str === undefined) return '""';
    const stringified = String(str);
    if (stringified.includes(',') || stringified.includes('"') || stringified.includes('\n')) {
      return `"${stringified.replace(/"/g, '""')}"`;
    }
    return `"${stringified}"`;
  };

  const exportActionableLeakageCSV = () => {
    if (!detailed_leakage || detailed_leakage.length === 0) return;
    const headers = ['Case No', 'ZinniaX Case ID', 'Date of Surgery', 'Tech Status', 'ZinniaX Claim ID', 'Claim Number', 'Payer Type', 'Carrier Name', 'Claim Status'];
    const csvRows = detailed_leakage.map(row => [
      escapeCSV(row.case_no),
      escapeCSV(row.zinniax_case_id),
      escapeCSV(new Date(row.date_of_surgery).toLocaleDateString()),
      escapeCSV(row.tech_status),
      escapeCSV(row.zinniax_claim_id),
      escapeCSV(row.claim_number),
      escapeCSV(row.payer_type),
      escapeCSV(row.carrier_name),
      escapeCSV(row.claim_status)
    ].join(','));
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Actionable_Leakage_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportNoClaimsCSV = () => {
    if (!no_claims_list || no_claims_list.length === 0) return;
    const headers = ['Case No', 'ZinniaX Case ID', 'Date of Surgery', 'Tech Status', 'Insurance Type'];
    const csvRows = no_claims_list.map(row => [
      escapeCSV(row.case_no),
      escapeCSV(row.zinniax_case_id),
      escapeCSV(new Date(row.date_of_surgery).toLocaleDateString()),
      escapeCSV(row.tech_status),
      escapeCSV(row.insurance_type)
    ].join(','));
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Cases_Without_Claims_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Process payer leakage data for the stacked bar chart
  // We need to group by date, and have each payer_type as a key
  const processPayerLeakage = () => {
    const grouped = {};
    const payers = new Set();

    payer_leakage.forEach(item => {
      if (!grouped[item.date]) {
        grouped[item.date] = { date: item.date };
      }
      grouped[item.date][item.payer_type] = item.case_count;
      payers.add(item.payer_type);
    });

    // Convert to array and sort chronologically
    const result = Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
    return { data: result, payers: Array.from(payers) };
  };

  const { data: payerChartData, payers: uniquePayers } = processPayerLeakage();

  // Process no claims insurance data for the stacked bar chart
  const processNoClaimsInsurance = () => {
    if (!no_claims_insurance) return { data: [], insuranceTypes: [] };
    
    const grouped = {};
    const insuranceTypes = new Set();

    no_claims_insurance.forEach(item => {
      if (!grouped[item.date]) {
        grouped[item.date] = { date: item.date };
      }
      grouped[item.date][item.insurance_type] = item.case_count;
      insuranceTypes.add(item.insurance_type);
    });

    // Convert to array and sort chronologically
    const result = Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
    return { data: result, insuranceTypes: Array.from(insuranceTypes) };
  };

  const { data: noClaimsChartData, insuranceTypes: uniqueInsuranceTypes } = processNoClaimsInsurance();

  // Generate distinct colors for payers
  const COLORS = ['#E53E3E', '#DD6B20', '#D69E2E', '#38A169', '#319795', '#3182CE', '#2B6CB0', '#805AD5', '#D53F8C', '#B83280'];

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="container.xl" mx="auto" py={8} px={4}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="lg" mb={2} color="gray.800">ZinniaX Reconciliation</Heading>
            <Text color="gray.600">
              Tracking case flow from EHR (ZinniaX) to Revedy to Billing.
            </Text>
          </Box>
        <Select 
          w="200px" 
          value={daysFilter} 
          onChange={(e) => setDaysFilter(Number(e.target.value))}
          bg="white"
        >
          <option value={7}>Last 7 Days</option>
          <option value={14}>Last 14 Days</option>
          <option value={30}>Last 30 Days</option>
          <option value={60}>Last 60 Days</option>
          <option value={90}>Last 90 Days</option>
        </Select>
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue" onChange={(index) => {
        if (index === 2) fetchJobQueue();
      }}>
        <TabList mb={4}>
          <Tab>Dashboard</Tab>
          <Tab>Data Assistant</Tab>
          <Tab>Job Queue</Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            {/* Variance */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
        <Card bg="red.50">
          <CardBody>
            <Stat>
              <StatLabel fontSize="md" color="red.700">Missing from Revedy (Older than 14 Days)</StatLabel>
              <StatNumber fontSize="3xl" color="red.600">{variance.missing_from_revedy.toLocaleString()}</StatNumber>
              <StatHelpText color="red.600">In Zinnia with a claim, but not in Revedy</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg="orange.50">
          <CardBody>
            <Stat>
              <StatLabel fontSize="md" color="orange.700">Cases without Claims (Older than 5 Days)</StatLabel>
              <StatNumber fontSize="3xl" color="orange.600">{variance.cases_without_claims.toLocaleString()}</StatNumber>
              <StatHelpText color="orange.600">In Zinnia, but no Claim found</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Time Series Chart */}
      <Card mb={8}>
        <CardHeader>
          <Heading size="md">Daily Case Flow</Heading>
        </CardHeader>
        <Divider />
        <CardBody>
          <Box h="400px" w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={time_series} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(tick) => {
                    const date = new Date(tick);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Legend />
                <Bar dataKey="total_zinnia_cases" name="Total Zinnia Cases" fill="#CBD5E0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cases_in_revedy" name="Cases in Revedy" fill="#3182CE" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* Payer Leakage Chart */}
      <Card mb={8}>
        <CardHeader>
          <Heading size="md">Leakage by Payer (Missing from Revedy)</Heading>
          <Text fontSize="sm" color="gray.500">Cases that exist in ZinniaX but were never processed by Revedy, grouped by Payer Type.</Text>
        </CardHeader>
        <Divider />
        <CardBody>
          <Box h="400px" w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={payerChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(tick) => {
                    const date = new Date(tick);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Legend />
                {uniquePayers.map((payer, index) => (
                  <Bar 
                    key={payer} 
                    dataKey={payer} 
                    name={payer} 
                    stackId="a" 
                    fill={COLORS[index % COLORS.length]} 
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* No Claims Chart */}
      <Card mb={8}>
        <CardHeader>
          <Heading size="md">Cases without Claims by Insurance Type</Heading>
          <Text fontSize="sm" color="gray.500">Daily volume of cases in ZinniaX that do not have any associated claims, grouped by Insurance Type.</Text>
        </CardHeader>
        <Divider />
        <CardBody>
          <Box h="400px" w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={noClaimsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(tick) => {
                    const date = new Date(tick);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Legend />
                {uniqueInsuranceTypes.map((insuranceType, index) => (
                  <Bar 
                    key={insuranceType} 
                    dataKey={insuranceType} 
                    name={insuranceType} 
                    stackId="a" 
                    fill={COLORS[index % COLORS.length]} 
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* Detailed Leakage Table */}
      <Card mb={8}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="md">Actionable Leakage (Older than 14 Days)</Heading>
              <Text fontSize="sm" color="gray.500">Cases with generated claims that occurred more than two weeks ago but are still missing from Revedy.</Text>
            </Box>
            <Button onClick={exportActionableLeakageCSV} colorScheme="green" size="sm" isDisabled={!detailed_leakage || detailed_leakage.length === 0}>
              Export CSV
            </Button>
          </Flex>
        </CardHeader>
        <Divider />
        <CardBody>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Case No</Th>
                  <Th>ZinniaX Case ID</Th>
                  <Th>Date of Surgery</Th>
                  <Th>Tech Status</Th>
                  <Th>ZinniaX Claim ID</Th>
                  <Th>Claim Number</Th>
                  <Th>Payer Type</Th>
                  <Th>Carrier Name</Th>
                  <Th>Claim Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {detailed_leakage.length === 0 ? (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={4}>No actionable leakage found for this period.</Td>
                  </Tr>
                ) : (
                  detailed_leakage.map((caseItem, index) => (
                    <Tr key={index}>
                      <Td fontWeight="bold">{caseItem.case_no}</Td>
                      <Td>{caseItem.zinniax_case_id}</Td>
                      <Td>{new Date(caseItem.date_of_surgery).toLocaleDateString()}</Td>
                      <Td>
                        <Badge colorScheme={caseItem.tech_status === 'Tech Completed' ? 'green' : 'yellow'}>
                          {caseItem.tech_status || 'Unknown'}
                        </Badge>
                      </Td>
                      <Td>{caseItem.zinniax_claim_id}</Td>
                      <Td>{caseItem.claim_number || 'N/A'}</Td>
                      <Td>{caseItem.payer_type}</Td>
                      <Td>{caseItem.carrier_name}</Td>
                      <Td>{caseItem.claim_status || 'N/A'}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Detailed No Claims Table */}
      <Card mb={8}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="md">Cases without Claims (Older than 5 Days)</Heading>
              <Text fontSize="sm" color="gray.500">Detailed list of cases in ZinniaX that occurred more than 5 days ago but do not have any associated claims.</Text>
            </Box>
            <Button onClick={exportNoClaimsCSV} colorScheme="green" size="sm" isDisabled={!no_claims_list || no_claims_list.length === 0}>
              Export CSV
            </Button>
          </Flex>
        </CardHeader>
        <Divider />
        <CardBody>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Case No</Th>
                  <Th>ZinniaX Case ID</Th>
                  <Th>Date of Surgery</Th>
                  <Th>Tech Status</Th>
                  <Th>Insurance Type</Th>
                </Tr>
              </Thead>
              <Tbody>
                {no_claims_list && no_claims_list.length === 0 ? (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={4}>No cases without claims found for this period.</Td>
                  </Tr>
                ) : (
                  no_claims_list && no_claims_list.map((caseItem, index) => (
                    <Tr key={index}>
                      <Td fontWeight="bold">{caseItem.case_no}</Td>
                      <Td>{caseItem.zinniax_case_id}</Td>
                      <Td>{new Date(caseItem.date_of_surgery).toLocaleDateString()}</Td>
                      <Td>
                        <Badge colorScheme={caseItem.tech_status === 'Tech Completed' ? 'green' : 'yellow'}>
                          {caseItem.tech_status || 'Unknown'}
                        </Badge>
                      </Td>
                      <Td>{caseItem.insurance_type || 'Unknown'}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      </TabPanel>

          <TabPanel p={0}>
            {/* Data Agent Chat */}
            <Box mb={8}>
              <Heading size="md" mb={4}>Ask the Data</Heading>
              <Text color="gray.600" mb={4}>
                Ask complex questions about your ZinniaX data. The AI will write and execute SQL queries to find the exact answers.
              </Text>
              <DataAgentChat 
                contextType="zinniax_reconciliation" 
                height="600px"
              />
            </Box>
          </TabPanel>

          <TabPanel p={0}>
            {/* Job Queue */}
            <Card mb={8}>
              <CardHeader>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="md">Job Queue</Heading>
                    <Text fontSize="sm" color="gray.500">Recent cases queued for external processing.</Text>
                  </Box>
                  <Button onClick={fetchJobQueue} isLoading={queueLoading} colorScheme="blue" size="sm">
                    Refresh Queue
                  </Button>
                </Flex>
              </CardHeader>
              <Divider />
              <CardBody>
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th cursor="pointer" onClick={() => handleSort('id')}>Job ID {sortConfig.key === 'id' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('status')}>Status {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('case_no')}>Case No {sortConfig.key === 'case_no' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('zinniax_case_id')}>ZinniaX Case ID {sortConfig.key === 'zinniax_case_id' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('date_of_surgery')}>Date of Surgery {sortConfig.key === 'date_of_surgery' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('payer_type')}>Payer Group {sortConfig.key === 'payer_type' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('carrier_name')}>Carrier {sortConfig.key === 'carrier_name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th cursor="pointer" onClick={() => handleSort('created_at')}>Queued At {sortConfig.key === 'created_at' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</Th>
                        <Th>Associated Claims</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {getSortedJobQueue().length === 0 ? (
                        <Tr>
                          <Td colSpan={9} textAlign="center" py={4}>No jobs in the queue.</Td>
                        </Tr>
                      ) : (
                        getSortedJobQueue().map((job, index) => (
                          <Tr key={index}>
                            <Td fontSize="xs" color="gray.500">{job.id.substring(0, 8)}...</Td>
                            <Td>
                              <Badge 
                                colorScheme={
                                  job.status === 'completed' ? 'green' : 
                                  job.status === 'failed' ? 'red' : 
                                  job.status === 'processing' ? 'purple' : 
                                  'yellow'
                                }
                              >
                                {job.status}
                              </Badge>
                            </Td>
                            <Td fontWeight="bold">
                              <Button 
                                variant="link" 
                                colorScheme="blue" 
                                onClick={() => handleViewRawData(job.payload?.zinniax_case_id)}
                                isDisabled={!job.payload?.zinniax_case_id}
                              >
                                {job.payload?.case_no || 'Unknown'}
                              </Button>
                            </Td>
                            <Td>{job.payload?.zinniax_case_id || 'Unknown'}</Td>
                            <Td>{job.enriched_data?.date_of_surgery ? new Date(job.enriched_data.date_of_surgery).toLocaleDateString() : 'Unknown'}</Td>
                            <Td>{job.enriched_data?.payer_type || 'Unknown'}</Td>
                            <Td>{job.enriched_data?.carrier_name || 'Unknown'}</Td>
                            <Td>{new Date(job.created_at).toLocaleString()}</Td>
                            <Td>
                              {job.payload?.claims?.map((claim, i) => (
                                <Badge key={i} mr={2} colorScheme="blue">
                                  {claim.claim_number} ({claim.status})
                                </Badge>
                              ))}
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Raw Data Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ZinniaX Case Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {rawLoading ? (
              <Flex justify="center" align="center" h="200px">
                <Spinner size="xl" color="blue.500" />
              </Flex>
            ) : selectedRawData && !selectedRawData.error ? (
              <Box>
                {/* Formatted Summary for NMA Team */}
                <Box mb={6}>
                  <Heading size="sm" mb={3} color="blue.600">Case Summary</Heading>
                  <SimpleGrid columns={3} spacing={4} bg="gray.50" p={4} borderRadius="md" borderWidth="1px">
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Case Number</Text>
                      <Text>{selectedRawData.case?.caseNo || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Date of Surgery</Text>
                      <Text>{selectedRawData.case?.dateOfSurgery ? new Date(selectedRawData.case.dateOfSurgery).toLocaleDateString() : 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Tech Status</Text>
                      <Badge colorScheme={selectedRawData.case?.status === 'Tech Completed' ? 'green' : 'yellow'}>
                        {selectedRawData.case?.status || 'Unknown'}
                      </Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Patient Name</Text>
                      <Text>{selectedRawData.case?.patientName || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Patient DOB</Text>
                      <Text>{selectedRawData.case?.patientDOB ? new Date(selectedRawData.case.patientDOB).toLocaleDateString() : 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">MRN</Text>
                      <Text>{selectedRawData.case?.medicalRecordNo || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Surgeon</Text>
                      <Text>{selectedRawData.case?.doctorName || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Procedure</Text>
                      <Text>{selectedRawData.case?.procedure || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Hospital</Text>
                      <Text>{selectedRawData.case?.hospitalName || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Tech</Text>
                      <Text>{selectedRawData.case?.techName || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Reader</Text>
                      <Text>{selectedRawData.case?.readerName || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Audit Status</Text>
                      <Text>{selectedRawData.case?.auditStatus || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Primary Insurance</Text>
                      <Text>{selectedRawData.case?.primaryInsuranceTypeName || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Carrier</Text>
                      <Text>{selectedRawData.case?.primaryInsuranceCarrierName || 'N/A'}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Box mb={6}>
                  <Heading size="sm" mb={3} color="blue.600">Associated Claims ({selectedRawData.claims?.length || 0})</Heading>
                  {selectedRawData.claims?.length > 0 ? (
                    <TableContainer borderWidth="1px" borderRadius="md">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Claim Number</Th>
                            <Th>Type</Th>
                            <Th>Status</Th>
                            <Th>Payer</Th>
                            <Th>Carrier</Th>
                            <Th isNumeric>Billed Amount</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {selectedRawData.claims.map((claim, idx) => (
                            <Tr key={idx}>
                              <Td fontWeight="bold">{claim.claimNumber}</Td>
                              <Td>{claim.claimType}</Td>
                              <Td>
                                <Badge colorScheme={claim.status === 'OPEN' ? 'blue' : claim.status === 'PROCESSING' ? 'purple' : 'gray'}>
                                  {claim.status}
                                </Badge>
                              </Td>
                              <Td>{claim.insuranceTypeName}</Td>
                              <Td>{claim.carrierName}</Td>
                              <Td isNumeric>${claim.billedAmount?.toFixed(2) || '0.00'}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Text color="gray.500" fontStyle="italic">No claims found for this case.</Text>
                  )}
                </Box>
              </Box>
            ) : (
              <Text color="red.500">{selectedRawData?.error || 'No data available.'}</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      </Box>
    </Box>
  );
};

export default ZinniaxReconciliationDashboard;
